import React, { forwardRef, useContext } from "react";

import { AlertContext } from "../context/alert";
import { CheckYesIcon } from "../icons/check_yes";
import { ExclaimCircleIcon } from "../icons/exclaim_circle";
import { InfoIcon } from "../icons/info";
import { XCloseIcon } from "../icons/x_close";
import { XNoIcon } from "../icons/x_no";

import { Grid } from "./grid";
import { IconButton } from "./icon_button";

const colorMap = {
  error: ["red", "redTint90"],
  info: ["blue", "blueTint90"],
  success: ["green", "greenTint90"],
  warn: ["orange", "orangeTint90"],
  new: ["blackTint90", "white"],
};

// In order for the bottom of the alert text to align with bottom of the new banner text, we add a minor offset to the
// top margin to account for the padding/font size differences.
const newTopOffset = "2px";
const NewBanner = () => (
  <span
    style={{
      backgroundColor: "var(--green-shade-20)",
      color: "white",
      borderRadius: "3px",
      position: "relative",
    }}
    className="body-xs bold py1 px2"
  >
    New
  </span>
);

const iconMap = {
  error: XNoIcon,
  info: InfoIcon,
  success: CheckYesIcon,
  warn: ExclaimCircleIcon,
  new: NewBanner,
};

const Alert = forwardRef<any, any>((props, ref) => {
  const {
    children,
    className,
    id,
    kind,
    pre = true,
    showClose = true,
    iconOnly = false,
    onClose,
    ...rest
  } = props;

  const [color, bgColor] = colorMap[kind];
  const AlertIcon = iconMap[kind];

  const alertContext = useContext(AlertContext);

  return (
    <Grid
      alignItems="start"
      bgColor={iconOnly ? null : bgColor}
      border={iconOnly ? null : { color }}
      borderRadius="4px"
      className={className}
      columns="max-content 1fr max-content"
      columnGap={2}
      data-testid={props["data-testid"] || (kind ? `alert-${kind}` : "alert")}
      pb={2}
      pt={2}
      px={3}
      ref={ref}
      {...rest}
    >
      <AlertIcon color={color} mt="2px" size="lg" />
      <div
        className="body-s"
        style={kind === "new" ? { marginTop: newTopOffset } : {}}
      >
        {children}
      </div>
      {showClose ? (
        <IconButton onClick={handleClose}>
          <XCloseIcon />
        </IconButton>
      ) : null}
    </Grid>
  );

  function handleClose() {
    if (alertContext?.removeAlert) {
      alertContext.removeAlert(id);
    }
    if (onClose) {
      onClose(id);
    }
  }
});

export { Alert };
